import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Sparkles, Send, Plus, Trash2, RefreshCw, Database, ListTodo, Megaphone, Check } from "lucide-react";

const LEEG_ANTWOORD = "Claude denkt mee…";

export default function Strategie() {
  const [sessies, setSessies] = useState([]);
  const [sessieId, setSessieId] = useState(null);
  const [berichten, setBerichten] = useState([]);
  const [vraag, setVraag] = useState("");
  const [bezig, setBezig] = useState(false);
  const [stroom, setStroom] = useState("");
  const [startvragen, setStartvragen] = useState([]);
  const [ctxInfo, setCtxInfo] = useState(null);
  const [gedaan, setGedaan] = useState({});
  const bodem = useRef(null);

  const laadSessies = async () => {
    const { data } = await api.get("/strategie/sessies");
    setSessies(data || []);
    return data || [];
  };

  const opentSessie = async (sid) => {
    setSessieId(sid);
    if (!sid) { setBerichten([]); return; }
    const { data } = await api.get(`/strategie/sessies/${sid}`);
    setBerichten(data.berichten || []);
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sv }, { data: ci }] = await Promise.all([
          api.get("/strategie/startvragen"),
          api.get("/strategie/context"),
        ]);
        setStartvragen(sv.vragen || []);
        setCtxInfo({ ...ci, model: sv.model });
        const s = await laadSessies();
        if (s.length) await opentSessie(s[0].id);
      } catch (e) {
        toast.error(e?.response?.status === 403 ? "Alleen admins kunnen de strategie-assistent gebruiken" : "Laden mislukt");
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { bodem.current?.scrollIntoView({ behavior: "smooth" }); }, [berichten, stroom]);

  const verversContext = async () => {
    try {
      const { data } = await api.get("/strategie/context", { params: { vers: true } });
      setCtxInfo(c => ({ ...data, model: c?.model }));
      toast.success("Cijfers opnieuw doorgerekend");
    } catch { toast.error("Verversen mislukt"); }
  };

  const nieuwGesprek = async () => {
    const { data } = await api.post("/strategie/sessies");
    await laadSessies();
    setSessieId(data.id);
    setBerichten([]);
  };

  const verwijder = async (sid) => {
    if (!window.confirm("Dit gesprek verwijderen?")) return;
    await api.delete(`/strategie/sessies/${sid}`);
    const s = await laadSessies();
    if (sid === sessieId) await opentSessie(s[0]?.id || null);
  };

  const stuur = async (tekst) => {
    const v = (tekst ?? vraag).trim();
    if (!v || bezig) return;
    setVraag("");
    setBezig(true);
    setStroom(LEEG_ANTWOORD);
    setBerichten(b => [...b, { id: `tmp-${Date.now()}`, rol: "user", tekst: v }]);
    try {
      const res = await fetch(`${API_BASE}/strategie/vraag`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: api.defaults.headers.common["Authorization"],
        },
        body: JSON.stringify({ vraag: v, sessie_id: sessieId }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const lezer = res.body.getReader();
      const dec = new TextDecoder();
      let buffer = "", antwoord = "";
      while (true) {
        const { value, done } = await lezer.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const delen = buffer.split("\n\n");
        buffer = delen.pop() || "";
        for (const blok of delen) {
          const dataRegel = blok.split("\n").find(l => l.startsWith("data: "));
          if (!dataRegel) continue;
          const payload = JSON.parse(dataRegel.slice(6));
          if (payload.sessie_id && !sessieId) setSessieId(payload.sessie_id);
          if (payload.delta) { antwoord += payload.delta; setStroom(antwoord.replace(/```acties[\s\S]*$/, "")); }
          if (payload.fout) toast.error(String(payload.fout).slice(0, 200));
          if (payload.bericht) {
            setBerichten(b => [...b, payload.bericht]);
            setStroom("");
          }
        }
      }
      await laadSessies();
    } catch (e) {
      toast.error("Claude antwoordde niet — probeer het opnieuw");
      setStroom("");
    } finally {
      setBezig(false);
    }
  };

  const zetVoorstel = async (actie, bericht) => {
    const pad = actie.type === "campagne" ? "/strategie/voorstel/campagne" : "/strategie/voorstel/taak";
    try {
      await api.post(pad, { titel: actie.titel, omschrijving: actie.omschrijving, waarom: actie.waarom, bericht_id: bericht.id });
      setGedaan(g => ({ ...g, [actie.id]: true }));
      toast.success(actie.type === "campagne" ? "Campagne-concept aangemaakt" : "Taak toegevoegd aan activiteiten");
    } catch (e) { toast.error(e?.response?.data?.detail || "Aanmaken mislukt"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1500px] mx-auto" data-testid="strategie-pagina">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Sparringpartner</div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-primary" /> Strategie
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Claude leest je echte cijfers mee — omzet, marges, voorraad en dekking, bol, campagnes,
            verkoopkansen en openstaande facturen — en denkt mee over de keuzes die geld opleveren.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={verversContext} data-testid="strategie-context-vernieuwen">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Cijfers verversen
          </Button>
          <Button size="sm" onClick={nieuwGesprek} data-testid="strategie-nieuw-gesprek">
            <Plus className="h-3.5 w-3.5 mr-1" />Nieuw gesprek
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-4" data-testid="strategie-context-info">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Database className="h-4 w-4 text-muted-foreground" />Meegestuurde cijfers
            </div>
            {ctxInfo ? (
              <>
                <p className="text-xs text-muted-foreground mt-2">
                  Doorgerekend {String(ctxInfo.gegenereerd_op || "").slice(0, 16).replace("T", " ")} ·{" "}
                  {Math.round((ctxInfo.tekens || 0) / 1000)}k tekens
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(ctxInfo.blokken || []).filter(b => !["jaar", "gegenereerd_op"].includes(b)).map(b => (
                    <span key={b} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{b}</span>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Model: {ctxInfo.model}</p>
              </>
            ) : <p className="text-xs text-muted-foreground mt-2">Laden…</p>}
          </div>

          <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="strategie-gesprekken">
            {sessies.map(s => (
              <div key={s.id} className={`p-3 flex items-start gap-2 ${s.id === sessieId ? "bg-secondary/60" : ""}`}>
                <button className="text-left flex-1 min-w-0" onClick={() => opentSessie(s.id)}
                  data-testid={`strategie-gesprek-${s.id}`}>
                  <div className="text-sm truncate">{s.titel}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {String(s.laatste_op || "").slice(0, 10)} · {s.berichten || 0} berichten
                  </div>
                </button>
                <button onClick={() => verwijder(s.id)} className="text-muted-foreground hover:text-destructive"
                  data-testid={`strategie-verwijder-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {sessies.length === 0 && <p className="p-4 text-xs text-muted-foreground">Nog geen gesprekken.</p>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5 min-h-[420px] space-y-5" data-testid="strategie-chat">
            {berichten.length === 0 && !stroom && (
              <div className="py-8">
                <p className="text-sm text-muted-foreground mb-3">Begin met een van deze vragen:</p>
                <div className="flex flex-wrap gap-2">
                  {startvragen.map(v => (
                    <button key={v} onClick={() => stuur(v)} disabled={bezig}
                      className="px-3 py-2 rounded-full text-xs border border-border bg-card hover:bg-secondary transition-colors"
                      data-testid={`strategie-startvraag-${v.slice(0, 12)}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {berichten.map(b => b.rol === "user" ? (
              <div key={b.id} className="flex justify-end">
                <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%] text-sm whitespace-pre-wrap"
                  data-testid="strategie-vraag-bubbel">{b.tekst}</div>
              </div>
            ) : (
              <div key={b.id} className="space-y-3" data-testid="strategie-antwoord">
                <Antwoordtekst tekst={b.tekst} />
                {(b.acties || []).length > 0 && (
                  <div className="border border-border rounded-xl divide-y divide-border">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                      Voorgestelde acties
                    </div>
                    {b.acties.map(a => (
                      <div key={a.id} className="p-3 flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">
                            {a.type === "campagne" ? <Megaphone className="h-3.5 w-3.5" /> : <ListTodo className="h-3.5 w-3.5" />}
                            {a.titel}
                          </div>
                          {a.omschrijving && <p className="text-xs text-muted-foreground mt-1">{a.omschrijving}</p>}
                          {a.waarom && <p className="text-xs text-muted-foreground/80 mt-1 italic">{a.waarom}</p>}
                        </div>
                        {gedaan[a.id] ? (
                          <Badge variant="secondary" className="shrink-0"><Check className="h-3 w-3 mr-1" />In het CRM</Badge>
                        ) : (
                          <Button size="sm" variant="outline" className="shrink-0" onClick={() => zetVoorstel(a, b)}
                            data-testid={`strategie-actie-${a.id}`}>
                            {a.type === "campagne" ? "Als campagne-concept" : "Als taak"}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {stroom && (
              <div data-testid="strategie-stroom"><Antwoordtekst tekst={stroom} /></div>
            )}
            <div ref={bodem} />
          </div>

          <div className="flex items-end gap-2">
            <Textarea value={vraag} onChange={(e) => setVraag(e.target.value)} rows={2}
              placeholder="Vraag bijvoorbeeld: waar zit mijn grootste marge-lek?"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); stuur(); } }}
              data-testid="strategie-invoer" />
            <Button onClick={() => stuur()} disabled={bezig || !vraag.trim()} data-testid="strategie-verstuur">
              <Send className="h-4 w-4 mr-1" />{bezig ? "Bezig…" : "Vraag"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Claude antwoordt in markdown; compact renderen in de huisstijl.
const MD = {
  h1: (p) => <h3 className="font-display text-xl font-medium mt-4 mb-1.5" {...p} />,
  h2: (p) => <h3 className="font-display text-xl font-medium mt-4 mb-1.5" {...p} />,
  h3: (p) => <h4 className="font-medium mt-3 mb-1" {...p} />,
  p: (p) => <p className="text-sm leading-relaxed mb-2" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 space-y-1 text-sm mb-2" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 space-y-1 text-sm mb-2" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-semibold" {...p} />,
  hr: () => <hr className="my-3 border-border" />,
  table: (p) => <div className="overflow-x-auto my-2"><table className="w-full text-xs border border-border" {...p} /></div>,
  th: (p) => <th className="text-left p-2 bg-secondary/50 border-b border-border" {...p} />,
  td: (p) => <td className="p-2 border-b border-border" {...p} />,
  code: (p) => <code className="text-xs font-mono bg-secondary px-1 py-0.5 rounded" {...p} />,
};

function Antwoordtekst({ tekst }) {
  return <div className="text-sm"><ReactMarkdown components={MD}>{tekst || ""}</ReactMarkdown></div>;
}
